import React, { useEffect, useState } from 'react';

import DeleteRounded from '@mui/icons-material/DeleteRounded';
import {
  Alert,
  Skeleton, Stack, Switch, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';

import ContentCard from '../../../../../components/ContentCard';
import Modal from '../../../../../components/Modal';
import { useGetClassesQuery } from '../../../../../services/classes';
import { Course } from '../../../../../services/courses/types';
import { useDeleteStreamMutation, useUpdateStreamMutation } from '../../../../../services/streams';
import { Stream } from '../../../../../services/streams/types';
import useError from '../../../../../utils/react/hooks/useError';
import useSuccess from '../../../../../utils/react/hooks/useSuccess';

import { ActiveStreamSettingContainer, DeleteButton } from './index.styles';

interface ActionsCardProps {
  stream?: Stream,
  course?: Course
}

const ActionsCard = ({ stream, course }: ActionsCardProps) => {
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const [confirmationModalState, setConfirmationModalState] = useState<{
    title: string;
    description: string;
    buttonLabel: string;
    buttonColor: 'error' | 'success';
  }>({
    title: '',
    description: '',
    buttonLabel: '',
    buttonColor: 'error',
  });

  const [deleteStream, {
    isSuccess: isDeleteStreamSuccess,
    isLoading: isDeleteStreamLoading,
    isError: isDeleteStreamError,
    error: deleteStreamError,
  }] = useDeleteStreamMutation();

  const [updateStreamActiveState, {
    isSuccess: isUpdateStreamSuccess,
    isLoading: isUpdateStreamLoading,
    isError: isUpdateStreamError,
    error: updateStreamError,
  }] = useUpdateStreamMutation();

  const { data: classesData } = useGetClassesQuery(
    {
      subject: course?.subject,
      course: stream?.course,
      stream: stream?.streamId,
    }, { skip: !course || !stream },
  );

  useEffect(() => {
    if (isDeleteStreamSuccess) {
      // Navigate up a level after a stream is deleted
      navigate(`/admin/course/${course?.subject}/${course?.id}`);
    }
  }, [isDeleteStreamSuccess]);

  // messages and labels for the comfirmation modal
  useEffect(() => {
    if (stream?.active) {
      setConfirmationModalState({
        title: 'Inactivate Stream',
        description: 'This will prevent classes from being added to this stream.',
        buttonLabel: 'Inactivate',
        buttonColor: 'error',
      });
    } else {
      setConfirmationModalState({
        title: 'Activate Stream',
        description: 'This will allow classes to be added to this stream.',
        buttonLabel: 'Activate',
        buttonColor: 'success',
      });
    }
  }, [stream?.active]);

  useSuccess(isDeleteStreamSuccess, 'Stream deleted');
  useError(isDeleteStreamError, deleteStreamError);

  useEffect(() => {
    if (isUpdateStreamSuccess) {
      setConfirmationModalOpen(false);
    }
  }, [isUpdateStreamSuccess]);

  useError(isUpdateStreamError, updateStreamError);
  useSuccess(isUpdateStreamSuccess, 'Stream updated');

  if (!course || !stream || !classesData) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Actions">
      <Stack direction="column" spacing={1}>
        {classesData.data.length > 0 && (
          <Alert severity="warning">
            There are active classes in this stream.
            This stream cannot be deleted or inactivated if there are active classes.
          </Alert>
        )}
        <DeleteButton
          color="error"
          size="medium"
          onClick={() => setDeleteModalOpen(true)}
          disabled={classesData.data.length > 0}
          startIcon={(<DeleteRounded />)}
        >
          Delete
        </DeleteButton>
        <ActiveStreamSettingContainer>
          <Typography>Inactive</Typography>
          <Switch
            checked={stream.active ?? true}
            onChange={() => setConfirmationModalOpen(true)}
            disabled={classesData.data.length > 0}
          />
          <Typography>Active</Typography>
        </ActiveStreamSettingContainer>
      </Stack>
      <Modal
        open={deleteModalOpen}
        handleClose={() => setDeleteModalOpen(false)}
        title="Are you sure?"
        description="This will permanently delete this course."
        buttons={[
          {
            label: 'Delete',
            onClick: () => deleteStream({
              course: stream.course,
              streamId: stream.streamId,
            }),
            color: 'error',
            loading: isDeleteStreamLoading,
          },
        ]}
      />
      <Modal
        open={confirmationModalOpen}
        handleClose={() => setConfirmationModalOpen(false)}
        title={confirmationModalState.title}
        description={confirmationModalState.description}
        buttons={[
          {
            label: confirmationModalState.buttonLabel,
            onClick: () => updateStreamActiveState({
              course: stream.course,
              streamId: stream.streamId,
              newAttributes: {
                active: !stream.active,
              },
            }),
            color: confirmationModalState.buttonColor,
            loading: isUpdateStreamLoading,
          },
        ]}
      />
    </ContentCard>
  );
};

export default ActionsCard;
